<template>
  <div
    class="price"
    :class="{
      'price--onsale': isOnSale,
      'price--sale-preview': showSalePreview,
    }"
  >
    <span class="price__original">{{ formattedOriginalPrice }}</span>
    <span v-if="showSalePreview" class="price__sale">
      {{ formattedSalePreviewPrice }}
    </span>
    <span v-else-if="isOnSale" class="price__sale">
      {{ formattedPrice }}
      <span v-if="showPercent">({{ percentageDiscount }}% off)</span>
    </span>
    <div v-if="showSaleStartText" class="price__sale-start">
      {{ saleStartText }}
    </div>
    <div v-if="shouldShowFinalSale" class="price__final-sale">
      <button
        type="button"
        class="final-sale-trigger"
        @click="openFinalSaleModal"
      >
        <span class="final-sale-trigger__label">Final Sale</span>
        <img
          src="@/assets/images/question-mark.svg"
          alt=""
          class="question-mark-icon"
        />
      </button>
    </div>
    <div v-if="shipsFromUkWarehouse" class="price__uk-warehouse">
      <button
        type="button"
        class="uk-warehouse-trigger"
        @click="openUkWarehouseModal"
      >
        <span class="uk-warehouse-trigger__label">Shipped from the UK</span>
        <img
          src="@/assets/images/question-mark.svg"
          alt=""
          class="question-mark-icon"
        />
      </button>
    </div>

    <SlideIn
      :open="finalSaleModalOpen"
      heading="Final Sale"
      @close="closeFinalSaleModal"
    >
      <template #content>
        <div class="final-sale-modal-content">
          <p>
            Final Sale Items: These are not eligible for return, except where
            required by law. For more details, please refer to our FAQs.
          </p>
        </div>
      </template>
    </SlideIn>
    <SlideIn
      :open="ukWarehouseModalOpen"
      heading="Shipped from the UK"
      @close="closeUkWarehouseModal"
    >
      <template #content>
        <div class="final-sale-modal-content">
          <p>{{ ukWarehouseDeliveryMessage }}</p>
        </div>
      </template>
    </SlideIn>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import SlideIn from "@/components/SlideIn.vue";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import { hasFinalSaleTag } from "@/helpers/finalSale.js";
import {
  hasUkWarehouseTag,
  UK_WAREHOUSE_DELIVERY_MESSAGE,
} from "@/helpers/ukWarehouse.js";
// import { salePreviewTimes } from "@/config.js";
export default {
  name: "ProductPrice",
  components: {
    SlideIn,
  },
  props: {
    showFinalSaleText: {
      type: Boolean,
      default: true,
    },
    showPercent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      finalSaleModalOpen: false,
      ukWarehouseModalOpen: false,
    };
  },
  computed: {
    ...mapGetters("product", [
      "isOnSale",
      "originalPrice",
      "price",
      "variant",
      "salePreviewPrice",
    ]),
    ...mapState("product", ["product"]),
    ...mapGetters("app", ["currencyCode"]),
    shouldShowFinalSale() {
      if (!this.showFinalSaleText) return false;

      const { finalSaleEnabled = false } = getFeatureToggles();
      if (!finalSaleEnabled) return false;

      return hasFinalSaleTag(this.product?.tags);
    },
    shipsFromUkWarehouse() {
      return hasUkWarehouseTag(this.product?.tags);
    },
    ukWarehouseDeliveryMessage() {
      return UK_WAREHOUSE_DELIVERY_MESSAGE;
    },
    showSalePreview() {
      // TODO: re-enable when sale preview is needed again
      // const { salePreviewEnabled = false } = getFeatureToggles();
      // return salePreviewEnabled && this.salePreviewPrice;
      return false;
    },
    showSaleStartText() {
      // TODO: re-enable when sale preview is needed again
      // const { salePreviewEnabled = false } = getFeatureToggles();
      // return salePreviewEnabled && this.salePreviewPrice;
      return false;
    },
    // TODO: re-enable when sale preview is needed again
    // saleStartText() {
    //   const currencyConfig = salePreviewTimes[this.currencyCode?.toLowerCase()];
    //   return currencyConfig?.displayText || "";
    // },
    formattedSalePreviewPrice() {
      if (!this.salePreviewPrice) return "";

      const isAud = this.currencyCode?.toLowerCase() === "aud";
      const prefix = isAud ? "$" : "";

      return `${prefix}${this.salePreviewPrice}`;
    },
    formattedPrice() {
      return this.$filters.toCurrency(
        this.price.amount,
        this.price.currencyCode,
        0
      );
    },
    formattedOriginalPrice() {
      if (this.isOnSale) {
        return this.$filters.toCurrency(
          this.originalPrice.amount,
          this.originalPrice.currencyCode,
          0
        );
      }

      return this.formattedPrice;
    },
    percentageDiscount() {
      return Math.round(
        ((this.originalPrice.amount - this.price.amount) /
          this.originalPrice.amount) *
          100
      );
    },
  },
  methods: {
    openFinalSaleModal() {
      this.finalSaleModalOpen = true;
    },
    closeFinalSaleModal() {
      this.finalSaleModalOpen = false;
    },
    openUkWarehouseModal() {
      this.ukWarehouseModalOpen = true;
    },
    closeUkWarehouseModal() {
      this.ukWarehouseModalOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.price {
  @include editorial-body(14px);

  &--onsale {
    .price__original {
      text-decoration: line-through;
    }
  }

  &--sale-preview {
    .price__original {
      text-decoration: none;
    }
  }

  &__original {
    margin-right: 8px;
  }

  &__sale {
    color: $color-red;
  }

  &__sale-start {
    color: $color-red;
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 135%;
    text-transform: uppercase;
    margin-top: 4px;
  }

  &__final-sale {
    display: block;
    margin-top: 4px;
    overflow: visible;

    .final-sale-trigger {
      @include utility-label(16px);
      color: $color-red;
      background: none;
      border: none;
      padding: 0;
      margin: 0;
      cursor: pointer;
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-weight: normal;
      line-height: 1.5;
      appearance: none;
      -webkit-appearance: none;

      &__label {
        display: inline-block;
        flex: 0 0 auto;
        color: inherit;
        line-height: 1.5;
        white-space: nowrap;
      }

      &:hover {
        opacity: 0.8;
      }

      .question-mark-icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
      }
    }
  }

  &__uk-warehouse {
    display: block;
    margin-top: 4px;
    overflow: visible;

    .uk-warehouse-trigger {
      @include utility-label(16px);
      color: $color-black;
      background: none;
      border: none;
      padding: 0;
      margin: 0;
      cursor: pointer;
      text-decoration: none;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-weight: normal;
      line-height: 1.5;
      appearance: none;
      -webkit-appearance: none;

      &__label {
        display: inline-block;
        flex: 0 0 auto;
        color: inherit;
        line-height: 1.5;
        white-space: nowrap;
      }

      &:hover {
        opacity: 0.8;
      }

      .question-mark-icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
      }
    }
  }
}

.final-sale-modal-content {
  text-align: left;

  p {
    @include utility-body(16px);
    color: $color-black;
    line-height: 1.5;
    margin: 0;
  }
}
</style>
